<template>
    <div v-if="!isTable" ref="sortableList" class="draggable-list">
        <slot />
        <div v-if="displayDragArea" :style="styleDragArea" class="drag-area" />
    </div>
    <tbody v-else ref="sortableList" class="draggable-list--tbody">
        <slot />
        <tr v-if="displayDragArea" :style="styleDragArea" class="drag-area">
            <td :colspan="100">
                <div class="drag-area-content" />
            </td>
        </tr>
    </tbody>
</template>

<script>
import Sortable from 'sortablejs';
import { mapState, mapActions } from 'pinia';
import { useSaasDraggableStore } from 'piniaStores/saasDraggableStore';

export default {
    name: 'SaasDraggable',
    props: {
        modelValue: {
            type: Array,
            required: true,
        },
        group: {
            type: String,
            default: 'default-group',
        },
        useDragArea: {
            type: Boolean,
            default: false,
        },
        dragClass: {
            type: String,
            default: 'draggable-item--draggable',
        },
        defaultDragZoneHeight: {
            type: String,
            default: '100px',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        filterClasses: {
            type: String,
            default: '',
        },
        isTable: {
            type: Boolean,
            default: false,
        },
        handle: {
            type: [String, null],
            default: null,
        },
    },
    emits: ['change', 'update:modelValue'],
    data() {
        return {
            heightItem: '0px',
            sortable: null,
            isDragging: false,
            upListener: null,
            cellWidths: [],
        };
    },
    computed: {
        ...mapState(useSaasDraggableStore, ['draggedItem']),
        styleDragArea() {
            return {
                height: this.heightItem,
            };
        },
        displayDragArea() {
            if (!this.useDragArea) {
                return false;
            }
            return !this.modelValue.length || (!!this.draggedItem && this.modelValue.length > 0);
        },
    },
    watch: {
        modelValue: {
            handler(newValue, oldValue) {
                if (newValue.length !== oldValue.length) {
                    this.$nextTick(() => {
                        this.computeStyle();
                    });
                }
            },
            deep: true,
        },
        disabled(newValue) {
            if (this.sortable) {
                this.sortable.option('disabled', newValue);
            }
        },
    },
    mounted() {
        this.sortable = Sortable.create(this.$refs.sortableList, {
            forceFallback: true,
            fallbackOffset: { x: 0, y: 0 },
            fallbackOnBody: true,
            filter: this.filterClasses,
            disabled: this.disabled,
            handle: this.handle,
            group: {
                name: this.group,
                pull: true,
                put: true,
            },
            onChoose: (event) => {
                this.isDragging = true;
                // Capture cell widths before the clone is created
                if (this.isTable) {
                    this.captureCellWidths(event.item);
                }
            },
            onUnchoose: this.cleanup,
            onStart: async (event) => {
                await this.updateDraggedItem(this.modelValue[event.oldIndex]);
                // Apply widths to the fallback clone element
                if (this.isTable) {
                    this.$nextTick(() => {
                        this.applyWidthsToFallback();
                    });
                }
            },
            onMove: () => {
                const draggedItem = this.draggedItem;
                this.emitChanges({ moved: this.draggedItem });
                if (draggedItem.editable !== undefined && !draggedItem.editable) {
                    return false;
                }
            },
            onUpdate: (event) => {
                if (event.oldIndex === event.newIndex) {
                    return;
                }
                const newIndex = event.newIndex;
                const itemsCopy = [...this.modelValue];

                itemsCopy.splice(event.oldIndex, 1);
                itemsCopy.splice(newIndex, 0, this.draggedItem);

                this.$emit('update:modelValue', itemsCopy);
                this.emitChanges({ updated: this.draggedItem });
            },
            onAdd: (event) => {
                // Remove the placeholder node added by Sortable.js to avoid duplicates
                this.removeNode(event.item);

                const draggedItem = this.draggedItem;
                const newIndex = event.newIndex;
                const itemsCopy = [...this.modelValue];
                itemsCopy.splice(newIndex, 0, draggedItem);

                this.$emit('update:modelValue', itemsCopy);
                this.emitChanges({ added: this.draggedItem });
            },
            onRemove: (event) => {
                const itemsCopy = [...this.modelValue];
                itemsCopy.splice(event.oldIndex, 1);
                this.$emit('update:modelValue', itemsCopy);

                this.emitChanges({ removed: this.draggedItem });
            },
            onEnd: async () => {
                await this.updateDraggedItem(null);
            },
        });
        this.computeStyle();
    },
    beforeUnmount() {
        if (this.sortable) {
            this.sortable.destroy();
            this.sortable = null;
        }
        this.cleanup();
    },
    methods: {
        ...mapActions(useSaasDraggableStore, ['updateDraggedItem']),
        cleanup() {
            this.isDragging = false;
            const item = document.querySelector('.sortable-drag');
            if (item) {
                item.classList.remove('sortable-drag');
            }
        },
        computeStyle() {
            const draggableItems = document.getElementsByClassName(this.dragClass);

            for (const draggableItem of draggableItems) {
                draggableItem.classList.add('saas-item--draggable');
                if (this.disabled) {
                    draggableItem.classList.add('sortable-disabled');
                } else {
                    draggableItem.classList.remove('sortable-disabled');
                }
            }

            const draggableItem = draggableItems.item(0);
            this.heightItem = draggableItem
                ? `${draggableItem.offsetHeight}px`
                : this.defaultDragZoneHeight;
        },
        emitChanges(event) {
            this.$nextTick(() => {
                this.$emit('change', event);
            });
        },
        removeNode(node) {
            if (node.parentElement !== null) {
                node.parentElement.removeChild(node);
            }
        },
        captureCellWidths(row) {
            const cells = row.querySelectorAll('td');
            this.cellWidths = Array.from(cells).map((cell) => cell.offsetWidth);
        },
        applyWidthsToFallback() {
            const fallback = document.querySelector('.sortable-fallback');
            if (fallback && this.cellWidths.length) {
                const cells = fallback.querySelectorAll('td');
                for (let i = 0; i < cells.length; i++) {
                    if (this.cellWidths[i] !== undefined) {
                        cells[i].style.width = `${this.cellWidths[i]}px`;
                    }
                }
            }
        },
    },
};
</script>

<style lang="scss">
.saas-item--draggable:not(.sortable-disabled) {
    cursor: pointer;

    &.sortable-chosen {
        cursor: grabbing;
    }

    &.sortable-chosen.sortable-ghost {
        background-color: var(--colors-common-background-primary-subtle);
        border: none;
        cursor: grabbing;

        > * {
            visibility: hidden;
        }
    }
}

.draggable-list {
    display: flex;
    flex-direction: column;

    &--tbody {
        display: revert;
    }

    &:has(.sortable-chosen) > .saas-item--draggable:not(.sortable-disabled) {
        cursor: grabbing;
    }
}

body:has(.sortable-fallback) {
    user-select: none;
    cursor: grabbing;
}

.sortable-fallback {
    box-shadow: var(--box-shadows-small);
    cursor: grabbing;
    height: fit-content !important;
    list-style-type: none;
}

tr.sortable-fallback {
    display: table;
}
</style>
